import React from "react";
import { ActionIcon, Group, Paper, Stack, Text, Title } from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import { LoyaltyPreset } from "../../../types/loyalty";
import { formatCurrency } from "../../../utils/cashDrawerUtils";
import {
  ActionRewardsDraft,
  PersonalizeDraft,
  ValueRewardsDraft,
  PRESET_LABELS,
  presetDescription,
  presetShowsProducts,
  presetShowsServices,
  redeemRewardName,
  EARN_PER_AMOUNT,
  toNumber,
} from "./types";

export function SummaryCard({
  title,
  onEdit,
  editLabel,
  children,
}: {
  title: string;
  onEdit?: () => void;
  editLabel?: string;
  children: React.ReactNode;
}) {
  return (
    <Paper withBorder radius="md" p="lg">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Title order={4}>{title}</Title>
          {onEdit && (
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={onEdit}
              aria-label={editLabel ?? `Edit ${title}`}
            >
              <IconPencil size={18} />
            </ActionIcon>
          )}
        </Group>
        {children}
      </Stack>
    </Paper>
  );
}

export function SummaryRow({
  label,
  primary,
  secondary,
}: {
  label: string;
  primary: string;
  secondary?: string;
}) {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xl">
      <Text size="sm" fw={600}>
        {label}
      </Text>
      <Stack gap={0} align="flex-end">
        <Text size="sm">{primary}</Text>
        {secondary && (
          <Text size="sm" c="dimmed">
            {secondary}
          </Text>
        )}
      </Stack>
    </Group>
  );
}

export function PersonalizeSummaryCard({
  value,
  servicesRate,
  productsRate,
  onEdit,
}: {
  value: PersonalizeDraft;
  /** Per-type earn rates (from the value step / spend rules) — preset copy. */
  servicesRate?: number | string;
  productsRate?: number | string;
  onEdit?: () => void;
}) {
  const rewardsExpire = value.expirationPolicy !== "none";
  const daysCopy = rewardsExpire
    ? `Points expire after ${toNumber(value.expirationDays)} days`
    : "Points never expire";
  return (
    <SummaryCard title="Personalize your program" onEdit={onEdit}>
      <Stack gap="md">
        <SummaryRow
          label="Choose how clients get points:"
          primary={PRESET_LABELS[value.preset]}
          secondary={presetDescription(
            value.preset,
            servicesRate,
            productsRate,
          )}
        />
        <SummaryRow
          label="Do their rewards ever expire?"
          primary={rewardsExpire ? "Yes" : "No"}
          secondary={daysCopy}
        />
      </Stack>
    </SummaryCard>
  );
}

export function ValueRewardsSummaryCard({
  value,
  preset,
  onEdit,
}: {
  value: ValueRewardsDraft;
  preset: LoyaltyPreset;
  onEdit?: () => void;
}) {
  const dollar = formatCurrency(EARN_PER_AMOUNT);
  return (
    <SummaryCard title="Transaction Based Rewards" onEdit={onEdit}>
      <Stack gap="md">
        {presetShowsServices(preset) && (
          <SummaryRow
            label="Services earn:"
            primary={`${toNumber(value.servicesRate)} points per ${dollar}`}
          />
        )}
        {presetShowsProducts(preset) && (
          <SummaryRow
            label="Products earn:"
            primary={`${toNumber(value.productsRate)} points per ${dollar}`}
          />
        )}
        <SummaryRow
          label="Redemption value:"
          primary={`${formatCurrency(toNumber(value.redemptionIncrement.cashValue))} equals ${toNumber(
            value.redemptionIncrement.pointsCost,
          ).toLocaleString("en-US")} points`}
          secondary={redeemRewardName(
            value.redemptionIncrement.pointsCost,
            value.redemptionIncrement.cashValue,
          )}
        />
      </Stack>
    </SummaryCard>
  );
}

export function ActionRewardsSummaryCard({
  value,
  onEdit,
}: {
  value: ActionRewardsDraft;
  onEdit?: () => void;
}) {
  const rebookingSecondary = value.rebookingEnabled
    ? `Earns ${toNumber(value.rebookingPoints)} points`
    : undefined;
  const visitsSecondary = value.visitsEnabled
    ? `${toNumber(value.visitsPoints)} points earned, every ${toNumber(value.visitsInterval)} visits`
    : undefined;
  const firstAppointmentSecondary = value.firstAppointmentEnabled
    ? `Earns ${toNumber(value.firstAppointmentPoints)} points`
    : undefined;
  return (
    <SummaryCard title="Action Based Rewards" onEdit={onEdit}>
      <Stack gap="md">
        <SummaryRow
          label="Immediate Rebooking (within 24 hours)"
          primary={value.rebookingEnabled ? "Enabled" : "Disabled"}
          secondary={rebookingSecondary}
        />
        <SummaryRow
          label="Visits"
          primary={value.visitsEnabled ? "Enabled" : "Disabled"}
          secondary={visitsSecondary}
        />
        <SummaryRow
          label="First appointment"
          primary={value.firstAppointmentEnabled ? "Enabled" : "Disabled"}
          secondary={firstAppointmentSecondary}
        />
      </Stack>
    </SummaryCard>
  );
}
